/**
 * Stripe Payment Integration Service
 *
 * This service handles Stripe Checkout for subscription management using Payment Links
 *
 * SETUP INSTRUCTIONS:
 * 1. Go to https://dashboard.stripe.com/test/payment-links
 * 2. Click "New payment link" for each subscription plan
 * 3. Configure each plan:
 *    - Monthly Plan: $29/month recurring
 *    - 3-Month Plan: $69 every 3 months recurring
 *    - 6-Month Plan: $119 every 6 months recurring
 * 4. Copy each Payment Link URL
 * 5. Add to your .env file:
 *    REACT_APP_STRIPE_PAYMENT_LINK_MONTHLY=https://buy.stripe.com/test_...
 *    REACT_APP_STRIPE_PAYMENT_LINK_3MONTH=https://buy.stripe.com/test_...
 *    REACT_APP_STRIPE_PAYMENT_LINK_6MONTH=https://buy.stripe.com/test_...
 * 6. Restart the development server
 *
 * Note: Payment Links are the modern, no-backend-required way to accept payments
 * Stripe Dashboard: https://dashboard.stripe.com
 * Docs: https://stripe.com/docs/payment-links
 */

// Stripe Configuration for Payment Links
const STRIPE_CONFIG = {
  paymentLinks: {
    monthly: process.env.REACT_APP_STRIPE_PAYMENT_LINK_MONTHLY || '',
    quarterly: process.env.REACT_APP_STRIPE_PAYMENT_LINK_3MONTH || '',
    biannual: process.env.REACT_APP_STRIPE_PAYMENT_LINK_6MONTH || ''
  }
};

/**
 * Check if Stripe is properly configured
 */
export const isStripeConfigured = () => {
  return Boolean(
    STRIPE_CONFIG.paymentLinks.monthly &&
    STRIPE_CONFIG.paymentLinks.quarterly &&
    STRIPE_CONFIG.paymentLinks.biannual
  );
};

/**
 * Redirect to Stripe Checkout for subscription using Payment Links
 * @param {string} planId - Plan ID (e.g., 'monthly', 'quarterly', 'biannual')
 * @param {string} customerEmail - Customer's email address (optional, prefill if available)
 * @returns {Promise<void>}
 */
export const redirectToCheckout = async (planId, customerEmail) => {
  try {
    const paymentLink = STRIPE_CONFIG.paymentLinks[planId];

    if (!paymentLink) {
      throw new Error(`Invalid plan ID: ${planId}. Please configure payment links.`);
    }

    // Add email as query parameter if provided (Stripe will prefill the email field)
    const url = customerEmail
      ? `${paymentLink}?prefilled_email=${encodeURIComponent(customerEmail)}`
      : paymentLink;

    // Redirect to Stripe Payment Link
    window.location.href = url;
  } catch (error) {
    console.error('Error redirecting to Stripe Checkout:', error);
    throw error;
  }
};

/**
 * Create a Stripe Customer Portal session
 * This allows customers to manage their subscription, update payment methods, etc.
 * Note: Requires server-side implementation to generate portal session
 * @param {string} customerId - Stripe Customer ID
 * @returns {Promise<string>} Portal URL
 */
export const getCustomerPortalUrl = async (customerId) => {
  // This would typically call your backend API endpoint
  // For now, return a placeholder
  console.warn('Customer Portal requires server-side implementation');
  return 'https://billing.stripe.com/session/...';
};

/**
 * Get Stripe Price IDs for plans
 * @returns {Object} Object containing price IDs
 */
export const getPriceIds = () => {
  return STRIPE_CONFIG.prices;
};

/**
 * Get plan details
 * @param {string} planId - Plan ID ('monthly', 'quarterly', 'biannual')
 * @returns {Object} Plan details
 */
export const getPlanDetails = (planId) => {
  const plans = {
    monthly: {
      id: 'monthly',
      name: 'Monthly',
      price: 29,
      interval: 'month',
      intervalCount: 1
    },
    quarterly: {
      id: 'quarterly',
      name: '3-Month Plan',
      price: 69,
      interval: '3 months',
      intervalCount: 3,
      savings: 18,
      savingsPercent: 21
    },
    biannual: {
      id: 'biannual',
      name: '6-Month Plan',
      price: 119,
      interval: '6 months',
      intervalCount: 6,
      savings: 55,
      savingsPercent: 32
    }
  };

  return plans[planId] || null;
};

export default {
  isStripeConfigured,
  redirectToCheckout,
  getCustomerPortalUrl,
  getPriceIds,
  getPlanDetails
};
